// Phase HUB-DRILL-1 — shared hook for fetches with a 15s timeout fallback.
// Eliminates infinite spinners: any fetch that exceeds 15s surfaces an
// error state with a Retry button.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

const DEFAULT_TIMEOUT_MS = 15_000;

export default function useTimedFetch(url, { params, timeoutMs = DEFAULT_TIMEOUT_MS, skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const tickRef = useRef(0);

  const paramsKey = JSON.stringify(params || {});

  const run = useCallback(async () => {
    if (!url || skip) return;
    const tick = ++tickRef.current;
    setLoading(true); setError(null);
    let timedOut = false;
    const t = setTimeout(() => {
      timedOut = true;
      if (tickRef.current === tick) {
        setError({
          message: "Request timed out. Tap Retry to try again.",
          code: "TIMEOUT",
        });
        setLoading(false);
      }
    }, timeoutMs);
    try {
      const r = await api.get(url, { params });
      if (tickRef.current !== tick || timedOut) return;
      setData(r.data); setError(null);
    } catch (e) {
      if (tickRef.current !== tick || timedOut) return;
      let msg = e?.response?.data?.detail || e?.userMessage || e?.message || "Failed to load";
      if (typeof msg === "object") msg = msg.detail || JSON.stringify(msg);
      setError({
        message: String(msg),
        code: e?.response?.status === 403 ? "FORBIDDEN"
              : e?.response?.status === 404 ? "NOT_FOUND" : "ERROR",
        status: e?.response?.status,
      });
    } finally {
      clearTimeout(t);
      if (tickRef.current === tick) setLoading(false);
    }
  }, [url, paramsKey, timeoutMs, skip]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { run(); }, [run]);

  return { data, loading, error, reload: run, setData };
}
